/**
 * ActivityTimeline — 24h hourly alert intensity, severity-stacked.
 *
 * Pulls up to 200 alerts from the last 24h and buckets them client-side
 * into 24 hourly slots (the API doesn't have an "hourly buckets" endpoint
 * yet — we'd add one if a tenant blew past 200 alerts/day, but for the
 * MVP this works on every realistic load).
 *
 * Each bucket is a vertical stacked bar: critical (red) on top, warning
 * (amber) below, info (cyan) at the base. Clicking a bucket navigates
 * to the Reports page filtered to that hour. Hover shows the breakdown.
 *
 * The 200-row cap is documented and surfaced via a small "+N more"
 * indicator when the result is paginated past the cap so operators
 * know the chart is incomplete.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { reportsApi, type LatestAlertRow } from '../../services/reportsApi';

const BUCKET_HOURS = 24;
const MAX_ALERTS_FETCHED = 200;

interface BucketEntry {
  hourStart: Date;
  critical: number;
  warning: number;
  info: number;
  total: number;
}

function bucketAlerts(alerts: LatestAlertRow[], from: Date): BucketEntry[] {
  // Pre-seed 24 empty buckets aligned to UTC hour boundaries from the
  // window start. Aligning to hour-of-day on the user's local time would
  // produce 25 buckets when DST shifts; UTC alignment keeps the chart
  // honest.
  const buckets: BucketEntry[] = [];
  const start = new Date(from);
  start.setUTCMinutes(0, 0, 0);
  for (let i = 0; i < BUCKET_HOURS; i++) {
    buckets.push({
      hourStart: new Date(start.getTime() + i * 3600_000),
      critical: 0,
      warning: 0,
      info: 0,
      total: 0,
    });
  }

  for (const a of alerts) {
    const created = new Date(a.createdAt);
    const idx = Math.floor((created.getTime() - start.getTime()) / 3600_000);
    if (idx < 0 || idx >= BUCKET_HOURS) continue;
    const b = buckets[idx];
    if (a.severity === 'CRITICAL') b.critical++;
    else if (a.severity === 'WARNING') b.warning++;
    else b.info++;
    b.total++;
  }

  return buckets;
}

export default function ActivityTimeline() {
  const navigate = useNavigate();

  const query = useQuery({
    queryKey: ['c2', 'timeline-24h'],
    queryFn: async () => {
      const to = new Date();
      const from = new Date(to.getTime() - BUCKET_HOURS * 3600_000);
      const page = await reportsApi.listAlerts(
        { from: from.toISOString(), to: to.toISOString() },
        1,
        MAX_ALERTS_FETCHED,
      );
      return { alerts: page.data, totalItems: page.pagination.totalItems, from, to };
    },
    refetchInterval: 30_000,
    staleTime: 25_000,
  });

  const buckets = useMemo(() => {
    if (!query.data) return [] as BucketEntry[];
    return bucketAlerts(query.data.alerts, query.data.from);
  }, [query.data]);

  const max = useMemo(
    () => buckets.reduce((m, b) => Math.max(m, b.total), 0),
    [buckets],
  );

  if (query.isLoading) {
    return (
      <div className="h-full p-3 flex items-end gap-1">
        {Array.from({ length: BUCKET_HOURS }).map((_, i) => (
          <div key={i} className="skeleton flex-1 h-2/3" />
        ))}
      </div>
    );
  }

  if (!query.data || query.data.totalItems === 0) {
    return (
      <div className="h-full flex items-center justify-center text-[12px] text-iron-400">
        No activity in the last 24 hours.
      </div>
    );
  }

  const truncated =
    query.data.totalItems > query.data.alerts.length
      ? query.data.totalItems - query.data.alerts.length
      : 0;

  return (
    <div className="h-full flex flex-col px-3 pt-2 pb-1">
      {/* Header strip — total + max-bucket marker */}
      <div className="flex items-center justify-between mb-1 text-[10px] font-mono uppercase tracking-[0.15em] text-iron-400">
        <div>
          24H · {query.data.totalItems.toLocaleString()} alerts
          {truncated > 0 && (
            <span className="ml-2 text-warn" title={`${truncated} alerts beyond the 200-row cap not visualized`}>
              +{truncated} not shown
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Legend color="bg-alert" label="CRIT" />
          <Legend color="bg-warn" label="WARN" />
          <Legend color="bg-sky" label="INFO" />
        </div>
      </div>

      {/* Bars */}
      <div className="flex-1 flex items-end gap-px relative">
        {buckets.map((b, i) => {
          const heightPct = max > 0 ? (b.total / max) * 100 : 0;
          const isEmpty = b.total === 0;
          return (
            <button
              key={i}
              type="button"
              onClick={() => {
                const from = b.hourStart.toISOString();
                const to = new Date(b.hourStart.getTime() + 3600_000).toISOString();
                navigate(`/reports?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`);
              }}
              title={`${b.hourStart.toISOString().slice(11, 16)}Z · ${b.total} alerts (${b.critical}c/${b.warning}w/${b.info}i)`}
              className={clsx(
                'flex-1 relative flex flex-col-reverse min-w-0 group transition-opacity',
                isEmpty
                  ? 'hover:bg-iron-800'
                  : 'cursor-pointer hover:opacity-90',
              )}
              style={{ height: '100%' }}
            >
              {/* Empty bucket — render a thin baseline so the chart shows
                  alignment. */}
              {isEmpty && (
                <div className="h-px w-full bg-iron-700 self-end" />
              )}
              {b.info > 0 && (
                <div
                  className="bg-sky w-full"
                  style={{ height: `${(b.info / b.total) * heightPct}%` }}
                />
              )}
              {b.warning > 0 && (
                <div
                  className="bg-warn w-full"
                  style={{ height: `${(b.warning / b.total) * heightPct}%` }}
                />
              )}
              {b.critical > 0 && (
                <div
                  className="bg-alert w-full"
                  style={{ height: `${(b.critical / b.total) * heightPct}%` }}
                />
              )}
            </button>
          );
        })}
      </div>

      {/* Hour ticks — every 4h to avoid clutter */}
      <div className="flex gap-px mt-1">
        {buckets.map((b, i) => {
          const showTick = i % 4 === 0;
          return (
            <div
              key={i}
              className="flex-1 text-center font-mono text-[9px] text-iron-500"
            >
              {showTick && b.hourStart.toISOString().slice(11, 13) + 'Z'}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className={clsx('inline-block w-2 h-2', color)} />
      <span>{label}</span>
    </span>
  );
}
